import { Capacitor } from '@capacitor/core';
import {
  type DeviceSyncProviderCredentials,
  DeviceSyncProviderCredentialsSchema,
} from '@am3nd-org/pointcraft-types';

import { SECURE_STORAGE_KEYS, secureStorageService } from './secure-storage.service';
import { invalidateStripeTerminalConnectionTokenScope } from './stripe-terminal-token-provider';

let webSessionCredentials: DeviceSyncProviderCredentials = [];

/**
 * Provider credentials must never enter the general device-sync cache. Native
 * builds persist them in Keychain/Keystore; web builds deliberately keep them
 * in memory because this secure-storage plugin's web fallback is localStorage.
 */
export async function replaceProviderCredentials(
  credentials: DeviceSyncProviderCredentials
): Promise<void> {
  const parsed = DeviceSyncProviderCredentialsSchema.parse(credentials);

  if (!Capacitor.isNativePlatform()) {
    webSessionCredentials = parsed;
    return;
  }

  await secureStorageService.set(
    SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS,
    JSON.stringify(parsed)
  );
}

export async function loadProviderCredentials(): Promise<DeviceSyncProviderCredentials> {
  if (!Capacitor.isNativePlatform()) return webSessionCredentials;

  const raw = await secureStorageService.get(SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS);
  if (!raw) return [];

  try {
    return DeviceSyncProviderCredentialsSchema.parse(JSON.parse(raw));
  } catch {
    await clearProviderCredentials();
    return [];
  }
}

export async function clearProviderCredentials(): Promise<void> {
  invalidateStripeTerminalConnectionTokenScope();
  webSessionCredentials = [];
  await secureStorageService.remove(SECURE_STORAGE_KEYS.DEVICE_SYNC_PROVIDER_CREDENTIALS);
}
